import React, { useCallback, useEffect, useState } from "react";
import { message, Tooltip } from "antd";

import { QmDimensionTable } from "../components/qmTable";
import { IKnowledgeType, IKnowledgeTypeListResponse } from "./types";
import { ITableFilter, ITableSort, QmKeyObject } from "../components/types";
import { remove, select } from "../actions/dimensionsAndType";
import { Utilitaries } from "../../../business";
import { downloadFile } from "../../../components/util";
import { PaginationConfig } from "antd/lib/pagination";
import { ColumnProps, SorterResult } from "antd/lib/table";
import { WrappedFormKnowledgeType } from "./formKnowledgeType";

const listName = "tipos_conhecimentos";

const REPORT_PARAMS = {
    accao: "lista",
    lista: listName,
    campos: "designacao:400:Tipo de conhecimento;descricao:400:Descrição;",
    titulo: "Tipos de conhecimentos",
};

export function KnowledgeType() {
    const [dataSource, setDataSource] = useState<IKnowledgeType[]>([]);
    const [loadingData, setLoadingData] = useState(true);
    const [showDeleteRecords, setShowDeleteRecords] = useState(false);
    const [filter, setFilter] = useState<ITableFilter>({
        pagination: {
            page: 1,
            perPage: 0,
        },
        filter: {},
    });
    const [editRow, setEditRow] = useState<IKnowledgeType | null>(null);
    const [showModalNew, setShowModalNew] = useState(false);

    /** get tipos conhecimentos from the server */
    const loadData = useCallback(() => {
        setLoadingData(true);
        select({
            accao: "lista",
            lista: "tipos_conhecimentos",
            pagina: 1,
            resultados_pagina: 0,
            mostra_lista_eliminados: showDeleteRecords,
        })
            .then((responses: IKnowledgeTypeListResponse) => {
                const list: IKnowledgeType[] = Utilitaries.toArray(responses.linha);
                const data = list.map((item) => {
                    return {
                        ...item,
                        designacao: typeof item.designacao === "object" ? "" : item.designacao,
                        descricao: typeof item.descricao === "object" ? "" : item.descricao,
                        versao: typeof item.versao === "object" ? "" : item.versao,
                    };
                });

                setDataSource(data);
                setLoadingData(false);
            })
            .catch(() => {
                message.error("Ocorreu um erro ao carregar os tipos de conhecimentos.");
            });
    }, [showDeleteRecords]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    function handleEdit(row: IKnowledgeType) {
        setEditRow(row);
    }

    function deleteRow(id: string) {
        remove({
            accao: "eliminar",
            lista: listName,
            chave1: id,
        }).then((sucess) => {
            if (sucess) {
                message.success("Linha eliminada com sucesso");
                loadData();
            } else {
                message.error("Ocorreu um erro");
            }
        });
    }

    async function onExportList(type: "pdf" | "xls") {
        const params = {
            ...REPORT_PARAMS,
            filter,
            saida: type,
            downloadInBrowser: true,
        };
        await downloadFile("gerelistas.php", params);
    }

    function toggleShowDelete(value: boolean) {
        setShowDeleteRecords(value);
    }

    function handleNew() {
        setShowModalNew(true);
    }

    function handleCloseNew(reload?: boolean) {
        if (reload) {
            loadData();
        }
        setShowModalNew(false);
    }

    function handleCloseEdit(reload?: boolean) {
        if (reload) {
            loadData();
        }
        setEditRow(null);
    }

    /** for search results in table */

    function handleTableChange(
        _: PaginationConfig,
        filters: Partial<Record<keyof IKnowledgeType, string[]>>,
        sorter: SorterResult<IKnowledgeType>
    ) {
        const objFilter: QmKeyObject = {};

        if (filters) {
            Object.entries(filters).forEach(([key, val]) => {
                if (val && val[0]) objFilter[key] = val[0];
            });
        }

        let sortObj: ITableSort | null = null;
        if (sorter.columnKey && sorter.order) {
            sortObj = {
                sorterBy: sorter.columnKey,
                sorterOrder: sorter.order,
            };
        }

        setFilter((state) => ({
            ...state,
            pagination: {
                ...state.pagination,
                page: 1,
            },
            filter: objFilter,
            sorter: sortObj ?? undefined,
        }));
    }
    /****************************** */

    const columns: ColumnProps<IKnowledgeType>[] = [
        {
            title: "Tipo de conhecimento",
            dataIndex: "designacao",
            width: "30%",
            ...QmDimensionTable.columnsSearch("designacao"),
        },
        {
            title: "Descrição",
            dataIndex: "descricao",
            ...QmDimensionTable.columnsSearch("descricao"),
            width: "50%",
            render: (text) => {
                text = typeof text !== "object" ? text : "";
                return text && text.length >= 100 ? (
                    <Tooltip title={text}>
                        <div
                            style={{
                                textOverflow: "ellipsis",
                                width: " 500px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                            }}
                        >
                            {text}
                        </div>
                    </Tooltip>
                ) : (
                    <div>{text}</div>
                );
            },
        },
        {
            title: "Ações",
            dataIndex: "actions",
            width: "20%",
            render: (_, record) =>
                record._eliminado_ === "t" ? (
                    <QmDimensionTable.actions
                        deleteRow={() => deleteRow(record.id_tipo_conhecimento)}
                        handleEdit={() => handleEdit(record)}
                    />
                ) : null,
        },
    ];

    return (
        <>
            <QmDimensionTable.list
                rowKey={(record) => record.id_tipo_conhecimento}
                loading={loadingData}
                columns={columns}
                dataSource={dataSource}
                exportList={onExportList}
                toogleShowDeletedRecords={toggleShowDelete}
                handleNew={handleNew}
                onChange={handleTableChange}
            />
            {showModalNew && <WrappedFormKnowledgeType onClose={handleCloseNew} />}
            {editRow && <WrappedFormKnowledgeType record={editRow} onClose={handleCloseEdit} />}
        </>
    );
}
